import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Outlet, useNavigate, useParams } from "react-router-dom";

import { Patient } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { isFinalClientLoadError, searchPatientWithRetry } from "@/clients/services/client-chart";
import { ensurePatientMrn } from "@/clients/utils/mrn";
import { getPatientPracticeId } from "@/clients/utils/patient-practice";
import { isNewerVersion } from "@/clients/utils/patient-version";
import { ORGANIZATION_ID } from "@/constants/local-storage";
import { OrganizationsContext } from "@/contexts/organizations";
import { PatientContext } from "@/contexts/patient";
import { fetchAndDispatchPregnancyRecords } from "@/pregnancy/services/record";
import { AppDispatch } from "@/store";
import { getActiveOrganizationId } from "@/utils/medplum";
import { showSystemErrorNotification, toast } from "@/utils/notifications";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

export default function ClientContainer() {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const dispatch = useDispatch<AppDispatch>();
  const { organizations } = useContext(OrganizationsContext);
  const { clientId } = useParams() as { clientId: string };
  const [patient, setPatient] = useState<Patient | undefined>(undefined);
  const [loadedChart, setLoadedChart] = useState<LoadedChart | undefined>(undefined);

  const organizationId = getActiveOrganizationId();

  useEffect(() => {
    let isCancelled = false;
    // Drops the chart of another client, so the chart stays hidden during this load.
    setLoadedChart((current) => (current?.clientId === clientId ? current : undefined));
    setPatient((current) => (current?.id === clientId ? current : undefined));
    (async () => {
      try {
        if (!clientId) return;

        const client = await searchPatientWithRetry(medplum, clientId, () => isCancelled);
        if (isCancelled) return;

        const practiceId = getPatientPracticeId(client);

        if (
          !client ||
          !practiceId ||
          organizations.every((organization) => organization.id !== practiceId)
        ) {
          navigate(`/practices/${organizationId}/clients`);
          return;
        }

        if (practiceId !== organizationId) {
          localStorage.setItem(ORGANIZATION_ID, practiceId);
          window.location.reload();
          return;
        }

        setLoadedChart({ clientId, practiceId });
        setPatient(client);
        fetchAndDispatchPregnancyRecords(medplum, dispatch, clientId);
        // Backfill missing MRNs on load only. Duplicate MRNs on legacy patients are remediated on
        // bot write paths (add-client, intake), not on chart open, to avoid search/write on every load.
        try {
          const updatedClient = await ensurePatientMrn(medplum, client, {
            remediateCollisions: false,
          });
          // Applies the backfilled Patient only when it is newer than the Patient on screen.
          setPatient((current) =>
            isNewerVersion(updatedClient, current) ? updatedClient : current,
          );
        } catch (error) {
          console.warn("MRN backfill failed on client chart load; continuing without MRN", {
            clientId,
            error,
          });
          if (isCancelled) return;
          toast({
            type: "warning",
            title: "Medical record number unavailable",
            message:
              "This chart opened without an MRN. Support can retry assignment on the next client update.",
            autoClose: false,
          });
        }
      } catch (error) {
        if (isCancelled) return;
        // No access, no session, or no such Patient: leave the chart the same way a missing client does.
        if (isFinalClientLoadError(error)) {
          navigate(`/practices/${organizationId}/clients`);
          return;
        }
        captureExceptionWithoutPhi("Failed to load client chart", error, { clientId });
        showSystemErrorNotification();
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [clientId, dispatch, medplum, navigate, organizationId, organizations]);

  // Show the chart only once this container loaded the client in the URL under the active practice,
  // and the Patient on screen is that client. Until then, the chart is still loading, retrying,
  // redirecting, or reloading into another practice. The practice comes from what the load
  // recorded, so a child route that replaces the Patient without its compartment keeps the chart.
  if (
    patient?.id !== clientId ||
    loadedChart?.clientId !== clientId ||
    loadedChart.practiceId !== organizationId
  ) {
    return <Loading />;
  }
  return (
    <PatientContext.Provider value={{ patient, setPatient }}>
      <Outlet />
    </PatientContext.Provider>
  );
}

/** The client and practice the chart's Patient was loaded for. */
interface LoadedChart {
  clientId: string;
  practiceId: string;
}
